import { useMemo, useState } from "react";
import { Link, createRoute, useNavigate } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import { usePlotloungeRoomsQuery } from "@/lib/staff/queries";
import {
  ALL_STATES,
  type RoomStatus,
  type StaffRoomFilters,
} from "@/lib/staff/types";
import { statusLabel, timingText } from "@/lib/staff/format";
import { StatusBadge } from "@/components/staff/StatusBadge";
import { SegmentProgress } from "@/components/staff/SegmentProgress";
import styles from "./staff-plotlounge-rooms.module.scss";

const STATE_FILTERS: { id: "all" | RoomStatus; label: string }[] = [
  { id: "all", label: "All" },
  ...ALL_STATES.map((s) => ({ id: s, label: statusLabel(s) })),
];

function PlotloungeRoomsList() {
  const navigate = useNavigate();
  const [activeState, setActiveState] = useState<"all" | RoomStatus>("all");
  const [search, setSearch] = useState("");
  const [startsWithin7Days, setStartsWithin7Days] = useState(false);
  const [lowMembers, setLowMembers] = useState(false);

  const filters: StaffRoomFilters = useMemo(
    () => ({
      states: activeState === "all" ? [] : [activeState],
      search,
      startsWithin7Days,
      lowMembers,
    }),
    [activeState, search, startsWithin7Days, lowMembers],
  );

  const {
    data: rooms,
    isLoading,
    isError,
    error,
  } = usePlotloungeRoomsQuery(filters);

  return (
    <main className={styles.shell}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Plotlounge rooms</h1>
          <p className={styles.sub}>
            {rooms ? `${rooms.length} rooms` : "Loading rooms"} · operator view
          </p>
        </div>
        <Link to="/staff/plotlounge-rooms/new" className={styles.newRoomBtn}>
          + New room
        </Link>
      </header>

      <div className={styles.controls}>
        <label className={styles.searchWrap}>
          <span
            aria-hidden
            style={{ color: "var(--pl-color-foreground-tertiary)" }}
          >
            ⌕
          </span>
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Search by book title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search by book title"
          />
        </label>

        <div
          className={styles.filters}
          role="tablist"
          aria-label="Filter by state"
        >
          {STATE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={activeState === f.id}
              className={`${styles.chip} ${
                activeState === f.id ? styles.chipActive : ""
              }`}
              onClick={() => setActiveState(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className={styles.filters}>
          <button
            type="button"
            aria-pressed={startsWithin7Days}
            className={`${styles.chip} ${styles.chipFlag} ${
              startsWithin7Days ? styles.chipActive : ""
            }`}
            onClick={() => setStartsWithin7Days((v) => !v)}
          >
            Starts in 7 days
          </button>
          <button
            type="button"
            aria-pressed={lowMembers}
            className={`${styles.chip} ${styles.chipFlag} ${
              lowMembers ? styles.chipActive : ""
            }`}
            onClick={() => setLowMembers((v) => !v)}
          >
            Active · &lt;3 members
          </button>
        </div>
      </div>

      {isError ? (
        <div className={styles.errorBanner} role="alert">
          Failed to load rooms: {(error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.tHead}>
            <tr className={styles.tHeadRow}>
              <th>Room</th>
              <th>State</th>
              <th>Timing</th>
              <th>Progress</th>
              <th style={{ textAlign: "right" }}>Members</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {rooms?.map((r) => (
              <tr
                key={r.id}
                className={styles.row}
                onClick={() =>
                  navigate({
                    to: "/staff/plotlounge-rooms/$id",
                    params: { id: r.id },
                  })
                }
              >
                <td>
                  <div className={styles.titleCell}>
                    <span className={styles.roomTitle}>{r.name}</span>
                    <span className={styles.roomBook}>
                      {r.book.title} — {r.book.author}
                    </span>
                  </div>
                </td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td className={styles.dateCell}>
                  {timingText({
                    status: r.status,
                    startDate: r.startDate,
                    endDate: r.endDate,
                    daysUntilStart: r.daysUntilStart,
                    daysSinceCompletion: r.daysSinceCompletion,
                  })}
                </td>
                <td>
                  <SegmentProgress
                    current={r.currentSegment}
                    total={r.totalSegments}
                    highlightCurrent={r.status === "ACTIVE"}
                  />
                </td>
                <td className={styles.numCell}>
                  {r.maxMembers
                    ? `${r.memberCount} / ${r.maxMembers}`
                    : r.memberCount}
                </td>
                <td className={styles.idCell}>{r.id}</td>
              </tr>
            ))}
            {rooms && rooms.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No rooms match these filters.
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  Loading rooms…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className={styles.foot}>
          <span>
            {rooms ? `Showing ${rooms.length} of ${rooms.length}` : ""}
          </span>
          <span>Sorted by start date · descending</span>
        </div>
      </div>
    </main>
  );
}

export const plotloungeRoomsListRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/plotlounge-rooms",
  component: PlotloungeRoomsList,
});
